import { ReactElement } from "react";
import Head from "next/head";
import { MainLayout } from "@/containers/layout/LandingLayout";

import Hero from "@/components/first-time-buyers/Hero";
import Faq from "@/components/first-time-buyers/Faq";

import { Props } from "@/components/howitworks/components/types";
import FirstTimeBuyers from "@/components/first-time-buyers/FirstTimeBuyers";
import BetterPath from "@/components/first-time-buyers/BetterPath";
import LatestArticles from "@/components/blog/latestArticles/LatestArticles";
import getConfig from "next/config";
import ReferralBanner from "@/components/landing-new/ReferralBanner";

const FirstTimeBuyersPage = ({ theme = "dark" }: Props): JSX.Element => {
  const { publicRuntimeConfig } = getConfig();
  return (
    <>
      <Head>
        <title>Getting Onto The Property Ladder As A First Time Buyer</title>
        <link rel="canonical" href="https://www.keyzy.com/first-time-buyers" />
        <meta
          name="description" key="description"
          content="Keyzy's rent-to-buy solution helps first time buyers break out of the rental cycle and start working towards their own home."
        />
        <meta
          name="twitter:card"
          content="Keyzy's rent-to-buy solution helps first time buyers break out of the rental cycle and start working towards their own home."
          key="twcard"
        />
        <meta name="twitter:creator" content="keyzyhomes" key="twhandle" />
        <meta property="og:url" content="/" key="ogurl" />
        <meta
          property="og:image"
          content="/first-time-buyers/meta-og-keyzy-ftb.png"
          key="ogimage"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Keyzy" key="ogsitename" />
        <meta
          property="og:title"
          content="Getting Onto The Property Ladder As A First Time Buyer"
          key="ogtitle"
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_GB" />
        <meta
          property="og:description"
          content="Keyzy's rent-to-buy solution helps first time buyers break out of the rental cycle and start working towards their own home."
          key="ogdesc"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://keyzy.com/" },
                { "@type": "ListItem", "position": 2, "name": "First Time Buyers", "item": "https://keyzy.com/first-time-buyers" },
              ],
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "name": "Keyzy Rent-to-Buy for First Time Buyers",
              "description": "Keyzy's rent-to-buy solution helps first time buyers break out of the rental cycle and start working towards their own home.",
              "url": "https://www.keyzy.com/first-time-buyers",
              "provider": {
                "@type": "Organization",
                "name": "Keyzy",
                "url": "https://www.keyzy.com",
              },
              "areaServed": {
                "@type": "City",
                "name": "London",
              },
              "audience": {
                "@type": "Audience",
                "audienceType": "First Time Buyers",
              },
            }),
          }}
        />
      </Head>

      <div className="bg-white">
        <Hero />
        <FirstTimeBuyers />
      </div>
      <div className="bg-landing-denim">
        <BetterPath />
      </div>
      <div className="bg-white">
        <LatestArticles
          selectedCategory={2 as number}
          excludeCategories={publicRuntimeConfig.NEXT_BLOG_LANDLORD_CATEGORY_ID}
        />
        <Faq />
        <ReferralBanner />
      </div>
    </>
  );
};

FirstTimeBuyersPage.getLayout = function getLayout(page: ReactElement) {
  return <MainLayout>{page}</MainLayout>;
};

export default FirstTimeBuyersPage;
